// P-63 (D-239 sec.2): pure model of "is the OTE extension installed" for the install button.

export type ExtensionKind = 'employee' | 'customer';

/** What the extension answers to `{type:'ping'}` (null/undefined = no answer: not installed or not Chrome). */
export interface ExtensionPing {
  ok?: boolean;
  kind?: string;
  version?: string;
  connected?: boolean;
  user_id?: string | null;
}

export type InstallState =
  | { state: 'missing' }
  | { state: 'installed'; version: string; connected: boolean; sameUser: boolean }
  | { state: 'outdated'; version: string; latest: string; connected: boolean; sameUser: boolean };

/** -1 / 0 / 1 for dotted numeric versions ("0.1.10" > "0.1.9"). Garbage parts count as 0. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((x) => parseInt(x, 10) || 0);
  const pb = b.split('.').map((x) => parseInt(x, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

export function installState(
  ping: ExtensionPing | null | undefined,
  kind: ExtensionKind,
  latest: string | null | undefined,
  currentUserId?: string | null,
): InstallState {
  // An answer from the OTHER flavor (or an unreadable one) counts as not installed.
  if (!ping || ping.ok !== true || ping.kind !== kind || typeof ping.version !== 'string') {
    return { state: 'missing' };
  }
  const connected = ping.connected === true;
  const sameUser = connected && (!ping.user_id || !currentUserId || ping.user_id === currentUserId);
  if (latest && compareVersions(ping.version, latest) < 0) {
    return { state: 'outdated', version: ping.version, latest, connected, sameUser };
  }
  return { state: 'installed', version: ping.version, connected, sameUser };
}

/** Does the hand-off need to run: installed (or outdated) but signed out / signed in as someone else. */
export function needsHandoff(s: InstallState): boolean {
  return s.state !== 'missing' && !s.sameUser;
}

/** D-240: how the install button delivers the extension. Default is a zip download plus a guide;
 *  "store" is kept for later (set CHROME_STORE_URL_* on the server, no code change). */
export type InstallMode = 'store' | 'download' | 'none';

export function installMode(info: { store_url?: string | null; download_available?: boolean } | null | undefined): InstallMode {
  if (!info) return 'none';
  if (info.store_url) return 'store';
  return info.download_available ? 'download' : 'none';
}

/** D-240 sec.3: the slim "new version" strip. Only for a zip install (no auto update) and only when outdated. */
export function showUpdateBar(s: InstallState, mode: InstallMode): boolean {
  return s.state === 'outdated' && mode === 'download';
}

/** The text Chrome will not open from a link, so the guide offers to copy it. */
export const CHROME_EXTENSIONS_URL = 'chrome://extensions';

/** D-345: the ids the page talks to = the ones it already has + the ones the server announced (valid ids only, no duplicates, existing first). */
export function mergeExtensionIds(current: readonly string[], fromServer: readonly string[] | null | undefined): string[] {
  const out = [...current];
  for (const raw of fromServer ?? []) {
    const id = String(raw).trim().toLowerCase();
    if (/^[a-p]{32}$/.test(id) && !out.includes(id)) out.push(id);
  }
  return out;
}
